import { useEffect } from 'react';
import type { Comic } from '@/mocks/manga';
import { comics } from '@/mocks/manga';

interface Props {
  comic: Comic | null;
  onClose: () => void;
}

export default function ComicDetailModal({ comic, onClose }: Props) {
  useEffect(() => {
    if (!comic) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [comic, onClose]);

  if (!comic) return null;

  const related = comics.filter((c) => c.id !== comic.id && c.category === comic.category).slice(0, 3);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${comic.title} 详情`}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-xl bg-background-50 shadow-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="scroll-thin overflow-y-auto max-h-[90vh]">
          <div className="relative h-40 md:h-52">
            <img
              src={comic.cover}
              alt={`${comic.title} 作品横幅`}
              className="h-full w-full object-cover object-top opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 to-background-50/20" />
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50/80 text-foreground-700 transition-colors hover:bg-background-50"
              aria-label="关闭弹窗"
            >
              <i className="ri-close-line text-xl" />
            </button>
            <div className="absolute bottom-4 left-6 flex items-center gap-3">
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium text-background-50 ${
                  comic.status === '连载中' ? 'bg-secondary-500' : 'bg-foreground-600'
                }`}
              >
                {comic.status}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-star-fill" />
                {comic.rating} 分
              </span>
            </div>
          </div>

          <div className="px-6 pb-8 pt-4 md:px-8">
            <h3 className="font-heading text-2xl font-bold text-foreground-950">{comic.title}</h3>
            <p className="mt-1 text-sm text-foreground-500">{comic.genre} · {comic.chapters}</p>

            <div className="mt-4 flex flex-wrap gap-2">
              {comic.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800"
                >
                  {t}
                </span>
              ))}
            </div>

            <h4 className="mt-6 text-sm font-semibold text-foreground-900">作品简介</h4>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comic.desc}</p>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-book-open-line text-primary-600" />
                最新章节速览
              </h4>
              <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {['最新话已上线', '人气情节持续走高', '口碑好评如潮'].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-sm text-foreground-600"
                  >
                    <i className="ri-checkbox-circle-fill text-secondary-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {related.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-foreground-900">同类推荐</h4>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {related.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3"
                    >
                      <img
                        src={r.cover}
                        alt={`${r.title} 封面`}
                        className="h-16 w-12 rounded-md object-cover"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground-900">{r.title}</p>
                        <p className="mt-1 text-xs text-foreground-500">{r.genre}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={onClose}
              className="mt-7 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              关闭详情
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}